import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TopNav from '../components/topbar/TopNav';
import SideMenu from '../components/sidebar/SideMenu';
import UserProfileModal from '../components/UserProfileModal';
import ClassSettingsModal from '../components/ClassSettingsModal';
import ClassMembersSettingsModal from '../components/ClassMembersSettingsModal';
import ClassLessonAlarmSettingsModal from '../components/classAlarm/ClassLessonAlarmSettingsModal';
import LearningContentWorksheetTemplateRegistryModal from '../components/learningWorksheet/LearningContentWorksheetTemplateRegistryModal';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { CalendarLinkedContextProvider } from '../contexts/CalendarLinkedContextProvider';
import { ClassSettingsModalContext } from '../contexts/ClassSettingsModalContext';
import { ClassMembersSettingsModalContext } from '../contexts/ClassMembersSettingsModalContext';
import { PaidPassGateProvider, usePaidPassGate } from '../contexts/PaidPassGateContext';
import { getAcademicTerms } from '../services/calendarService';
import { isProgramProgressFamilyLeaveDirty, isProgramProgressFamilyRoute, isProgramProgressRoute } from '../utils/programRoutes';
import { shouldIgnoreAppNavigationShortcut } from '../utils/appNavigationShortcutGuard';
import { installKeepAliveFocusGuard, releaseOrphanedBodyScrollLock, releaseTrappedKeepAliveFocus } from '../utils/keepAliveFocusRelease';
import { installUiSound } from '../utils/uiSound';
import { resolveNavigationAfterSelectIntegrated } from '../utils/sidebarClassView';
import { planFeatureForPath } from '../constants/planFeatures';
import { toISODate } from '../utils/date';
import { UserDisplayInfo } from '../services/authService';
import { useSlideImageStorageUsage } from '../hooks/useSlideImageStorageUsage';
import { useClassLessonAlarmService } from '../hooks/useClassLessonAlarmService';
import ClassLessonAlarmPopup from '../components/classAlarm/ClassLessonAlarmPopup';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { Grade } from '../types/domain';
import ProgramSlideCommunicationMonitorHost from '../components/program/ProgramSlideCommunicationMonitorHost';
import { TutorialProvider } from '../contexts/TutorialContext';
import TutorialOverlayHost from '../components/tutorial/TutorialOverlayHost';
import TutorialLauncherModal from '../components/tutorial/TutorialLauncherModal';
import { preloadAppImages } from '../utils/appImageCache';

function isTodoNoteRoute(path: string): boolean {
  return path === '/todo-note' || path.startsWith('/todo-note/');
}

function isBudgetNoteRoute(path: string): boolean {
  return (
    path === '/budget-note' ||
    path.startsWith('/budget-note/') ||
    path === '/admin/budget-note' ||
    path.startsWith('/admin/budget-note/')
  );
}

function isAssignmentPointsNoteRoute(path: string): boolean {
  return (
    path === '/assignment-note' ||
    path.startsWith('/assignment-note/') ||
    path === '/assignment-points-note' ||
    path.startsWith('/assignment-points-note/')
  );
}

/** CalendarLinkedContext 안에서 수업 알람 팝업을 마운트 */
const ClassLessonAlarmHost: React.FC<{
  workspaceId: string;
  grades: Grade[];
  currentGradeId: string | null;
  /** 이용권별 권한(class_alarm)으로 수업 알람이 잠겼으면 false */
  allowed?: boolean;
}> = ({ workspaceId, grades, currentGradeId, allowed = true }) => {
  const { popupOpen, popupInfo, handleStopAlarm, handleClosePopup, settings, presentationActive } =
    useClassLessonAlarmService({
      workspaceId,
      grades,
      currentGradeId,
      enabled: allowed && Boolean(workspaceId.trim()),
    });
  // '프레젠테이션 전용'이 켜져 있고 프레젠테이션 탭이 열려 있으면 메인 탭 팝업은 숨김(사운드·중계는 유지).
  // 즉시 실행(테스트) 팝업은 설정한 화면에서 바로 확인할 수 있게 숨기지 않는다.
  const isTestPopup = popupInfo?.channelLabel === '테스트';
  const suppressOnMain = settings.presentationOnly && presentationActive && !isTestPopup;
  return (
    <ClassLessonAlarmPopup
      open={popupOpen && !suppressOnMain}
      info={popupInfo}
      onStopAlarm={handleStopAlarm}
      onClosePopup={handleClosePopup}
    />
  );
};

interface MainLayoutProps {
  grades: Grade[];
  currentGradeId: string | null;
  onNavGradeChange: (gradeId: string) => void;
  userDisplay: UserDisplayInfo | null;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onUserClick?: () => void;
  profileModalOpen: boolean;
  onProfileModalClose: () => void;
  classSettingsModalOpen: boolean;
  onClassSettingsModalClose: () => void;
  onOpenClassSettings: () => void;
  classMembersSettingsModalOpen: boolean;
  onClassMembersSettingsModalClose: () => void;
  onOpenClassMembersSettings: () => void;
  workspaceId: string;
  onLogout: () => void;
  onUserDisplayUpdate: () => void;
  onDataChange: (options?: { selectGradeId?: string }) => void;
  children: React.ReactNode;
}

const MainLayoutInner: React.FC<MainLayoutProps> = ({
  grades,
  currentGradeId,
  onNavGradeChange,
  userDisplay,
  theme,
  onToggleTheme,
  onUserClick,
  profileModalOpen,
  onProfileModalClose,
  classSettingsModalOpen,
  onClassSettingsModalClose,
  onOpenClassSettings,
  classMembersSettingsModalOpen,
  onClassMembersSettingsModalClose,
  onOpenClassMembersSettings,
  workspaceId,
  onLogout,
  onUserDisplayUpdate,
  onDataChange,
  children,
}) => {
  const slideStorageEnabled = !!userDisplay;
  const { usage: slideImageStorageUsage } = useSlideImageStorageUsage(slideStorageEnabled);
  const navigate = useNavigate();
  const location = useLocation();
  const { selectProgramFocusGrade } = useProgramSidebar();

  /** 상단 네비 클래스 선택 → 사이드바 programFocusGradeId 동기화 */
  const handleTopNavGradeChange = useCallback(
    (gradeId: string) => {
      const id = gradeId.trim();
      if (!id) return;
      selectProgramFocusGrade(id);
      const params = new URLSearchParams(location.search);
      if (params.has('gradeId')) {
        params.set('gradeId', id);
        const q = params.toString();
        navigate({ pathname: location.pathname, search: q ? `?${q}` : '' }, { replace: true });
      }
    },
    [location.pathname, location.search, navigate, selectProgramFocusGrade]
  );

  /** 통합 보기로 바뀌면 클래스 전용 페이지에 머물지 않고 연간시간표(통합)로 이동 */
  const prevNavGradeIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const normalized = (currentGradeId ?? '').trim();
    const prev = prevNavGradeIdRef.current;
    prevNavGradeIdRef.current = normalized;
    if (prev === undefined) return;
    if (normalized !== '') return;
    if ((prev ?? '') === '') return;
    const next = resolveNavigationAfterSelectIntegrated(location.pathname, location.search);
    if (next.pathname === location.pathname && next.search === (location.search || '')) return;
    navigate({ pathname: next.pathname, search: next.search }, { replace: true });
  }, [currentGradeId, location.pathname, location.search, navigate]);

  /** keep-alive 탭 전환: 숨겨진 연간진도·프로그램 표 스크롤 래퍼 포커스가 cp-content 휠 스크롤을 가로채지 않게 */
  useLayoutEffect(() => {
    releaseTrappedKeepAliveFocus(location.pathname);
    releaseOrphanedBodyScrollLock();
  }, [location.pathname, location.key]);

  /** 숨은 keep-alive로 포커스가 다시 들어가면 휠 스크롤이 막힐 수 있어 상시 가드 */
  useEffect(() => installKeepAliveFocusGuard(), []);
  /** UI 효과음(기본 OFF, 햄버거 메뉴에서 토글) — 교사 앱에서만 */
  useEffect(() => installUiSound(), []);

  useEffect(() => {
    preloadAppImages();
  }, []);

  const { passGateStatus, isFeatureLocked, openPaidPassRequiredModal } = usePaidPassGate();

  /** 프리 이용권으로 제한 경로에 직접 진입 시 안내 후 홈으로 (동기화 확정 후에만) */
  useEffect(() => {
    if (passGateStatus !== 'ready') return;
    const paidFeature = planFeatureForPath(location.pathname);
    if (!paidFeature || !isFeatureLocked(paidFeature)) return;
    openPaidPassRequiredModal();
    // 대시보드(홈)까지 잠긴 이용권이면 홈 대신 이용권 페이지로(홈 ↔ 잠금 반복 방지)
    navigate(isFeatureLocked('dashboard') ? '/subscription' : '/', { replace: true });
  }, [passGateStatus, isFeatureLocked, location.pathname, navigate, openPaidPassRequiredModal]);

  /** 이용권별 권한: 대시보드(홈)가 잠긴 이용권이면 안내 후 이용권 페이지로 */
  useEffect(() => {
    if (passGateStatus !== 'ready') return;
    const path = location.pathname.replace(/\/$/, '') || '/';
    if (path !== '/' && path !== '/dashboard') return;
    if (!isFeatureLocked('dashboard')) return;
    openPaidPassRequiredModal();
    navigate('/subscription', { replace: true });
  }, [passGateStatus, isFeatureLocked, location.pathname, navigate, openPaidPassRequiredModal]);

  /** 이용권별 권한: 수업 알람(class_alarm) */
  const classAlarmLocked = isFeatureLocked('class_alarm');

  const {
    classMembersDirty,
    setClassMembersDirty,
    gradeTrackDirty,
    setGradeTrackDirty,
    academicCalendarDirty,
    setAcademicCalendarDirty,
    classScheduleDirty,
    setClassScheduleDirty,
    annualTimetableDirty,
    adminLearningDirty,
    workspaceLearningContentDirty,
    workspaceEvaluationPlanDirty,
    todoNoteDirty,
    budgetNoteDirty,
    assignmentPointsNoteDirty,
    evaluationPlanDirty,
    pendingLeaveTo,
    setPendingLeaveTo,
    saveClassMembersRef,
    saveGradeTrackRef,
    saveAcademicCalendarRef,
    saveClassScheduleRef,
    saveAnnualTimetableRef,
    saveAdminLearningRef,
    saveWorkspaceLearningContentRef,
    saveWorkspaceEvaluationPlanRef,
    saveTodoNoteRef,
    saveBudgetNoteRef,
    saveAssignmentPointsNoteRef,
    saveEvaluationPlanRef,
    programPendingLeaveTo,
    setProgramPendingLeaveTo,
    programProgressDirty,
    saveProgramProgressRef,
    setAnnualTimetableDirty,
    setAdminLearningDirty,
    setWorkspaceLearningContentDirty,
    setWorkspaceEvaluationPlanDirty,
    setTodoNoteDirty,
    setBudgetNoteDirty,
    setAssignmentPointsNoteDirty,
    setEvaluationPlanDirty,
    setProgramProgressDirty,
  } = useContext(GradeTrackLeaveContext);
  const classSettingsModalContextValue = useMemo(
    () => ({ openClassSettingsModal: onOpenClassSettings }),
    [onOpenClassSettings]
  );
  const classMembersSettingsModalContextValue = useMemo(
    () => ({ openClassMembersSettingsModal: onOpenClassMembersSettings }),
    [onOpenClassMembersSettings]
  );
  const [leaving, setLeaving] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [worksheetTemplateRegistryOpen, setWorksheetTemplateRegistryOpen] = useState(false);
  const [classLessonAlarmSettingsOpen, setClassLessonAlarmSettingsOpen] = useState(false);
  const [tutorialLauncherOpen, setTutorialLauncherOpen] = useState(false);
  const programLeaveWithoutRef = useRef<HTMLButtonElement>(null);
  const programLeaveSaveRef = useRef<HTMLButtonElement>(null);
  const pendingLeaveWithoutRef = useRef<HTMLButtonElement>(null);
  const pendingLeaveSaveRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isProgramProgressRoute(location.pathname)) {
      setProgramProgressDirty(false);
      setProgramPendingLeaveTo(null);
    }
    if (location.pathname !== '/class-members') setClassMembersDirty(false);
    if (location.pathname !== '/annual-timetable') setAnnualTimetableDirty(false);
    if (location.pathname !== '/admin/learning-content') setAdminLearningDirty(false);
    if (location.pathname !== '/workspace-learning-content') setWorkspaceLearningContentDirty(false);
    if (location.pathname !== '/workspace-evaluation-plan') setWorkspaceEvaluationPlanDirty(false);
    if (!isTodoNoteRoute(location.pathname)) setTodoNoteDirty(false);
    if (!isBudgetNoteRoute(location.pathname)) setBudgetNoteDirty(false);
    if (!isAssignmentPointsNoteRoute(location.pathname)) setAssignmentPointsNoteDirty(false);
    if (location.pathname !== '/admin/evaluation-plan') setEvaluationPlanDirty(false);
  }, [
    location.pathname,
    setAnnualTimetableDirty,
    setAdminLearningDirty,
    setWorkspaceLearningContentDirty,
    setWorkspaceEvaluationPlanDirty,
    setTodoNoteDirty,
    setBudgetNoteDirty,
    setAssignmentPointsNoteDirty,
    setEvaluationPlanDirty,
    setClassMembersDirty,
    setProgramProgressDirty,
    setProgramPendingLeaveTo,
  ]);

  useEffect(() => {
    // 라우트가 실제로 바뀐 뒤에는 이전 이탈 모달 상태를 정리해 화면 전환이 막히지 않게 함
    setPendingLeaveTo(null);
    setProgramPendingLeaveTo(null);
    setMobileSidebarOpen(false);
  }, [location.pathname, location.search, setPendingLeaveTo, setProgramPendingLeaveTo]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 900) setMobileSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getPageSaveRef = useCallback(() => {
    const path = location.pathname;
    if (isProgramProgressRoute(path)) return saveProgramProgressRef.current;
    if (path === '/class-members') return saveClassMembersRef.current;
    if (path === '/grade-track') return saveGradeTrackRef.current;
    if (path === '/academic-calendar') return saveAcademicCalendarRef.current;
    if (path === '/class-schedule') return saveClassScheduleRef.current;
    if (path === '/annual-timetable') return saveAnnualTimetableRef.current;
    if (path === '/admin/learning-content') return saveAdminLearningRef.current;
    if (path === '/workspace-learning-content') return saveWorkspaceLearningContentRef.current;
    if (path === '/workspace-evaluation-plan') return saveWorkspaceEvaluationPlanRef.current;
    if (path === '/admin/evaluation-plan') return saveEvaluationPlanRef.current;
    if (isTodoNoteRoute(path)) return saveTodoNoteRef.current;
    if (isBudgetNoteRoute(path)) return saveBudgetNoteRef.current;
    if (isAssignmentPointsNoteRoute(path)) return saveAssignmentPointsNoteRef.current;
    return null;
  }, [
    location.pathname,
    saveAcademicCalendarRef,
    saveAdminLearningRef,
    saveAnnualTimetableRef,
    saveAssignmentPointsNoteRef,
    saveBudgetNoteRef,
    saveEvaluationPlanRef,
    saveClassMembersRef,
    saveClassScheduleRef,
    saveGradeTrackRef,
    saveProgramProgressRef,
    saveTodoNoteRef,
    saveWorkspaceLearningContentRef,
    saveWorkspaceEvaluationPlanRef,
  ]);

  const handleLeaveWithoutSave = () => {
    if (!pendingLeaveTo) return;
    const path = location.pathname;
    if (path === '/class-members') {
      saveClassMembersRef.current?.discard?.();
      setClassMembersDirty(false);
    } else if (path === '/grade-track') {
      saveGradeTrackRef.current?.discard?.();
      setGradeTrackDirty(false);
    } else if (path === '/academic-calendar') {
      saveAcademicCalendarRef.current?.discard?.();
      setAcademicCalendarDirty(false);
    } else if (path === '/class-schedule') {
      saveClassScheduleRef.current?.discard?.();
      setClassScheduleDirty(false);
    } else if (path === '/annual-timetable') {
      saveAnnualTimetableRef.current?.discard?.();
      setAnnualTimetableDirty(false);
    }
    navigate(pendingLeaveTo);
    setPendingLeaveTo(null);
  };

  const handleSaveAndLeave = async () => {
    if (!pendingLeaveTo) return;
    setLeaving(true);
    try {
      await getPageSaveRef()?.save();
      navigate(pendingLeaveTo);
      setPendingLeaveTo(null);
    } catch {
      // 저장이 막히면(예: 멤버 번호 중복) 이동하지 않고 머문다. 안내는 각 페이지가 표시.
    } finally {
      setLeaving(false);
    }
  };

  const handleProgramLeaveWithoutSave = () => {
    if (programPendingLeaveTo) {
      saveProgramProgressRef.current?.discard?.();
      setProgramProgressDirty(false);
      navigate(programPendingLeaveTo);
      setProgramPendingLeaveTo(null);
    }
  };

  const handleProgramSaveAndLeave = async () => {
    if (!programPendingLeaveTo) return;
    setLeaving(true);
    try {
      await saveProgramProgressRef.current?.save();
      navigate(programPendingLeaveTo);
      setProgramPendingLeaveTo(null);
    } finally {
      setLeaving(false);
    }
  };

  const buildTodayAnnualProgressRoute = React.useCallback(async () => {
    const todayIso = toISODate(new Date());
    const currentYear = new Date().getFullYear();
    let targetTerm: 1 | 2 = Number(todayIso.slice(5, 7)) >= 8 ? 2 : 1;
    try {
      const terms = await getAcademicTerms(workspaceId, currentYear);
      const inRange = terms.find(
        (term) => todayIso >= term.starts_on && todayIso <= term.ends_on && (term.term_number === 1 || term.term_number === 2)
      );
      if (inRange) targetTerm = inRange.term_number;
    } catch {
      // 네트워크 오류 등으로 학기 조회가 실패해도 단축키는 기본 추정 학기로 이동.
    }
    const path = targetTerm === 1 ? '/annual-progress-term1' : '/annual-progress-term2';
    const params = new URLSearchParams();
    if (currentGradeId?.trim()) params.set('gradeId', currentGradeId.trim());
    params.set('openToday', String(Date.now()));
    const targetGradeId = currentGradeId?.trim() ?? '';
    return { to: `${path}?${params.toString()}`, path, targetGradeId };
  }, [currentGradeId, workspaceId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 's' && e.key !== 'S') return;
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (e.repeat) return;
      if (profileModalOpen || classSettingsModalOpen) return;
      if (pendingLeaveTo || programPendingLeaveTo) return;
      if (leaving) return;

      const saveRef = getPageSaveRef();
      if (saveRef?.save) {
        e.preventDefault();
        e.stopPropagation();
        void Promise.resolve(saveRef.save()).catch(() => undefined);
        return;
      }

      const visibleSaveBtn = Array.from(
        document.querySelectorAll<HTMLButtonElement>('.cp-btn-save-all')
      ).find((btn) => !btn.disabled && btn.offsetParent !== null);
      if (visibleSaveBtn) {
        e.preventDefault();
        e.stopPropagation();
        visibleSaveBtn.click();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [
    classSettingsModalOpen,
    getPageSaveRef,
    leaving,
    pendingLeaveTo,
    profileModalOpen,
    programPendingLeaveTo,
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyR' && e.code !== 'KeyH') return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (pendingLeaveTo || programPendingLeaveTo) return;
      e.preventDefault();
      e.stopPropagation();

      if (e.code === 'KeyH') {
        const to = '/dashboard';
        if (location.pathname === to) return;
        if (isProgramProgressFamilyRoute(location.pathname) && isProgramProgressFamilyLeaveDirty(programProgressDirty)) {
          setProgramPendingLeaveTo(to);
          return;
        }
        const dirty =
          (location.pathname === '/class-members' && classMembersDirty) ||
          (location.pathname === '/grade-track' && gradeTrackDirty) ||
          (location.pathname === '/academic-calendar' && academicCalendarDirty) ||
          (location.pathname === '/class-schedule' && classScheduleDirty) ||
          (location.pathname === '/annual-timetable' && annualTimetableDirty) ||
          (location.pathname === '/admin/learning-content' && adminLearningDirty) ||
          (location.pathname === '/admin/evaluation-plan' && evaluationPlanDirty) ||
          (location.pathname === '/workspace-learning-content' && workspaceLearningContentDirty) ||
          (location.pathname === '/workspace-evaluation-plan' && workspaceEvaluationPlanDirty) ||
          (isTodoNoteRoute(location.pathname) && todoNoteDirty) ||
          (isBudgetNoteRoute(location.pathname) && budgetNoteDirty) ||
          (isAssignmentPointsNoteRoute(location.pathname) && assignmentPointsNoteDirty);
        if (dirty) {
          setPendingLeaveTo(to);
          return;
        }
        navigate(to);
        return;
      }

      void (async () => {
        const { to, path, targetGradeId } = await buildTodayAnnualProgressRoute();
        const currentParams = new URLSearchParams(location.search);
        const currentGradeIdInUrl = (currentParams.get('gradeId') ?? '').trim();
        if (location.pathname === path && currentGradeIdInUrl === targetGradeId) {
          document.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'T',
              code: 'KeyT',
              shiftKey: true,
              bubbles: true,
              cancelable: true,
            })
          );
          return;
        }
        if (isProgramProgressFamilyRoute(location.pathname) && isProgramProgressFamilyLeaveDirty(programProgressDirty)) {
          setProgramPendingLeaveTo(to);
          return;
        }
        const dirty =
          (location.pathname === '/class-members' && classMembersDirty) ||
          (location.pathname === '/grade-track' && gradeTrackDirty) ||
          (location.pathname === '/academic-calendar' && academicCalendarDirty) ||
          (location.pathname === '/class-schedule' && classScheduleDirty) ||
          (location.pathname === '/annual-timetable' && annualTimetableDirty) ||
          (location.pathname === '/admin/learning-content' && adminLearningDirty) ||
          (location.pathname === '/admin/evaluation-plan' && evaluationPlanDirty) ||
          (location.pathname === '/workspace-learning-content' && workspaceLearningContentDirty) ||
          (location.pathname === '/workspace-evaluation-plan' && workspaceEvaluationPlanDirty) ||
          (isTodoNoteRoute(location.pathname) && todoNoteDirty) ||
          (isBudgetNoteRoute(location.pathname) && budgetNoteDirty) ||
          (isAssignmentPointsNoteRoute(location.pathname) && assignmentPointsNoteDirty);
        if (dirty) {
          setPendingLeaveTo(to);
          return;
        }
        navigate(to);
      })();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [
    academicCalendarDirty,
    annualTimetableDirty,
    adminLearningDirty,
    assignmentPointsNoteDirty,
    evaluationPlanDirty,
    workspaceLearningContentDirty,
    workspaceEvaluationPlanDirty,
    todoNoteDirty,
    budgetNoteDirty,
    buildTodayAnnualProgressRoute,
    classMembersDirty,
    classScheduleDirty,
    gradeTrackDirty,
    location.search,
    location.pathname,
    navigate,
    pendingLeaveTo,
    programPendingLeaveTo,
    programProgressDirty,
    setPendingLeaveTo,
    setProgramPendingLeaveTo,
  ]);

  useEffect(() => {
    const programOpen = programPendingLeaveTo !== null;
    const pendingOpen = pendingLeaveTo !== null;
    if (!programOpen && !pendingOpen) return;

    const withoutRef = programOpen ? programLeaveWithoutRef : pendingLeaveWithoutRef;
    const saveRef = programOpen ? programLeaveSaveRef : pendingLeaveSaveRef;

    const raf = window.requestAnimationFrame(() => {
      withoutRef.current?.focus();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      const w = withoutRef.current;
      const s = saveRef.current;
      if (!w || !s) return;

      if (e.key === 'Escape') {
        if (leaving) return;
        e.preventDefault();
        e.stopPropagation();
        if (programOpen) setProgramPendingLeaveTo(null);
        else setPendingLeaveTo(null);
        return;
      }

      if (e.key === 'Enter' && !e.isComposing) {
        if (leaving) return;
        e.preventDefault();
        e.stopPropagation();
        const active = document.activeElement;
        if (active === w || active === s) {
          (active as HTMLButtonElement).click();
        } else {
          w.focus();
        }
        return;
      }

      if (e.key === 'Tab') {
        if (leaving) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          if (document.activeElement === w) s.focus();
          else w.focus();
        } else {
          if (document.activeElement === s) w.focus();
          else s.focus();
        }
        return;
      }

      const arrow =
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown';
      if (!arrow) return;
      if (leaving) return;
      e.preventDefault();
      e.stopPropagation();
      const active = document.activeElement;
      if (active === s) w.focus();
      else s.focus();
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [
    leaving,
    pendingLeaveTo,
    programPendingLeaveTo,
    setPendingLeaveTo,
    setProgramPendingLeaveTo,
  ]);

  return (
    <div className="cp-layout-root">
      <TopNav
        grades={grades}
        currentGradeId={currentGradeId}
        workspaceId={workspaceId}
        onGradeChange={handleTopNavGradeChange}
        userDisplay={userDisplay}
        theme={theme}
        onToggleTheme={onToggleTheme}
        onUserClick={onUserClick}
        onLogout={onLogout}
        onOpenClassSettings={onOpenClassSettings}
        onOpenClassMembersSettings={onOpenClassMembersSettings}
        onOpenClassLessonAlarmSettings={() => {
          if (classAlarmLocked) {
            openPaidPassRequiredModal();
            return;
          }
          setClassLessonAlarmSettingsOpen(true);
        }}
        onOpenWorksheetTemplateRegistry={() => setWorksheetTemplateRegistryOpen(true)}
        onOpenTutorials={() => setTutorialLauncherOpen(true)}
        onMenuToggle={() => setMobileSidebarOpen((open) => !open)}
        mobileMenuOpen={mobileSidebarOpen}
        slideImageStorageUsage={slideImageStorageUsage}
      />
      <UserProfileModal
        isOpen={profileModalOpen}
        onClose={onProfileModalClose}
        userDisplay={userDisplay}
        workspaceId={workspaceId}
        onLogout={onLogout}
        onUserDisplayUpdate={onUserDisplayUpdate}
        onDataChange={onDataChange}
      />
      <ClassSettingsModal
        isOpen={classSettingsModalOpen}
        onClose={onClassSettingsModalClose}
        workspaceId={workspaceId}
        onDataChange={onDataChange}
      />
      <ClassMembersSettingsModal
        isOpen={classMembersSettingsModalOpen}
        onClose={onClassMembersSettingsModalClose}
        workspaceId={workspaceId}
        grades={grades}
        initialGradeId={currentGradeId}
      />
      <ClassLessonAlarmSettingsModal
        open={classLessonAlarmSettingsOpen}
        workspaceId={workspaceId}
        grades={grades}
        currentGradeId={currentGradeId}
        onClose={() => setClassLessonAlarmSettingsOpen(false)}
      />
      <LearningContentWorksheetTemplateRegistryModal
        open={worksheetTemplateRegistryOpen}
        workspaceId={workspaceId}
        onClose={() => setWorksheetTemplateRegistryOpen(false)}
      />
      <div className={`cp-layout-body${mobileSidebarOpen ? ' cp-layout-body-mobile-menu-open' : ''}`}>
        <aside className={`cp-sidebar-shell${mobileSidebarOpen ? ' is-open' : ''}`}>
          <SideMenu
            currentGradeId={currentGradeId}
            userDisplay={userDisplay}
            onOpenClassSettings={onOpenClassSettings}
            onOpenClassMembersSettings={onOpenClassMembersSettings}
          />
        </aside>
        {/* pathname·search를 main의 key로 쓰면 탭 전환 시 KeepAlive·Routes가 전부 리마운트되어 네트워크가 반복된다. */}
        <main className="cp-content">
          <CalendarLinkedContextProvider
            workspaceId={workspaceId}
            gradeId={(currentGradeId ?? '').trim()}
          >
            <ClassSettingsModalContext.Provider value={classSettingsModalContextValue}>
              <ClassMembersSettingsModalContext.Provider value={classMembersSettingsModalContextValue}>
                <ClassLessonAlarmHost
                  workspaceId={workspaceId}
                  grades={grades}
                  currentGradeId={currentGradeId}
                  allowed={!classAlarmLocked}
                />
                {children}
              </ClassMembersSettingsModalContext.Provider>
            </ClassSettingsModalContext.Provider>
          </CalendarLinkedContextProvider>
        </main>
      </div>
      {mobileSidebarOpen ? (
        <button
          type="button"
          className="cp-sidebar-mobile-backdrop"
          aria-label="메뉴 닫기"
          onClick={() => setMobileSidebarOpen(false)}
        />
      ) : null}

      {programPendingLeaveTo != null && (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-program-leave-modal-title"
          onClick={() => setProgramPendingLeaveTo(null)}
        >
          <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="cp-program-leave-modal-title" className="cp-leave-modal-title">
              저장 확인
            </h3>
            <p className="cp-leave-modal-desc">
              프로그램 테이블에 변경된 데이터가 있습니다. 저장 하지 않으면 데이터가 소실 될 수 있습니다. 저장하시겠습니까?
            </p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                ref={programLeaveWithoutRef}
                type="button"
                className="cp-btn-secondary"
                onClick={handleProgramLeaveWithoutSave}
                disabled={leaving}
              >
                저장하지 않고 이동
              </button>
              <button
                ref={programLeaveSaveRef}
                type="button"
                className="cp-btn-primary"
                onClick={handleProgramSaveAndLeave}
                disabled={leaving}
              >
                {leaving ? '저장 중…' : '저장하고 이동'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingLeaveTo != null && (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-leave-modal-title"
          onClick={() => setPendingLeaveTo(null)}
        >
          <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="cp-leave-modal-title" className="cp-leave-modal-title">
              변경사항이 저장되지 않았습니다
            </h3>
            <p className="cp-leave-modal-desc">
              저장하지 않고 나가시겠습니까?
            </p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                ref={pendingLeaveWithoutRef}
                type="button"
                className="cp-btn-secondary"
                onClick={handleLeaveWithoutSave}
                disabled={leaving}
              >
                저장하지 않고 나가기
              </button>
              <button
                ref={pendingLeaveSaveRef}
                type="button"
                className="cp-btn-primary"
                onClick={handleSaveAndLeave}
                disabled={leaving}
              >
                {leaving ? '저장 중…' : '저장 하기'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ProgramSlideCommunicationMonitorHost />
      <TutorialLauncherModal
        isOpen={tutorialLauncherOpen}
        onClose={() => setTutorialLauncherOpen(false)}
        workspaceId={workspaceId}
        gradeId={currentGradeId}
      />
      <TutorialOverlayHost />
    </div>
  );
};

const MainLayout: React.FC<MainLayoutProps> = (props) => (
  <PaidPassGateProvider>
    <TutorialProvider workspaceId={props.workspaceId}>
      <MainLayoutInner {...props} />
    </TutorialProvider>
  </PaidPassGateProvider>
);

export default MainLayout;

